import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  lang: 'en' | 'de' | 'fr' | 'it';
}

const seoData = {
    en: {
      title: 'TTStats - Swiss Table Tennis Rankings, ELO & Player Statistics | Switzerland',
      description: 'Track Swiss table tennis players, live ELO ratings, rankings and match statistics. Complete database of all Swiss table tennis players from click-tt.ch with advanced analytics, head-to-head comparisons, and performance tracking.',
      keywords: 'swiss table tennis, table tennis switzerland, swiss table tennis rankings, swiss table tennis players, elo rating table tennis, table tennis elo switzerland, click-tt, stt swiss table tennis, swiss table tennis statistics, table tennis rankings switzerland, swiss table tennis database, table tennis player search switzerland, schweiz tischtennis, suisse tennis de table',
      ogTitle: 'TTStats - Swiss Table Tennis Rankings & ELO Statistics',
      ogDescription: 'Complete Swiss table tennis player database with live ELO ratings, rankings, match history and advanced statistics. Track every player in Switzerland.'
    },
    de: {
      title: 'TTStats - Tischtennis Schweiz Rangliste, ELO & Spieler Statistiken',
      description: 'Verfolge Schweizer Tischtennisspieler, Live ELO-Wertungen, Ranglisten und Spielstatistiken. Vollständige Datenbank aller Schweizer Tischtennisspieler von click-tt.ch mit erweiterten Analysen, direkten Vergleichen und Leistungsverfolgung.',
      keywords: 'tischtennis schweiz, schweizer tischtennis, tischtennis rangliste schweiz, tischtennis spieler schweiz, tischtennis elo, elo rating tischtennis, click-tt schweiz, stt tischtennis, tischtennis statistik schweiz, tischtennis datenbank schweiz, tischtennisspieler suchen schweiz, tischtennis zürich, tischtennis bern, tischtennis ranking, schweizer tischtennisverband',
      ogTitle: 'TTStats - Tischtennis Schweiz Rangliste & ELO Statistiken',
      ogDescription: 'Vollständige Schweizer Tischtennis Spielerdatenbank mit Live ELO-Wertungen, Ranglisten, Spielhistorie und erweiterten Statistiken. Verfolge jeden Spieler in der Schweiz.'
    },
    fr: {
      title: 'TTStats - Tennis de table Suisse Classements, ELO & Statistiques Joueurs',
      description: 'Suivez les joueurs de tennis de table suisses, classements ELO en direct, rangs et statistiques de matchs. Base de données complète de tous les joueurs suisses de tennis de table de click-tt.ch avec analyses avancées, comparaisons directes et suivi des performances.',
      keywords: 'tennis de table suisse, suisse tennis de table, classement tennis de table suisse, joueurs tennis de table suisse, classement elo tennis de table, elo tennis de table suisse, click-tt suisse, stt tennis de table, statistiques tennis de table suisse, base de données tennis de table suisse, recherche joueur tennis de table suisse, tennis de table genève, tennis de table lausanne, fédération suisse tennis de table',
      ogTitle: 'TTStats - Tennis de table Suisse Classements & Statistiques ELO',
      ogDescription: 'Base de données complète des joueurs suisses de tennis de table avec classements ELO en direct, rangs, historique des matchs et statistiques avancées. Suivez chaque joueur en Suisse.'
    },
    it: {
      title: 'TTStats - Ping Pong Svizzera Classifiche, ELO & Statistiche Giocatori',
      description: 'Segui i giocatori di ping pong svizzeri, classifiche ELO in diretta, ranghi e statistiche delle partite. Database completo di tutti i giocatori svizzeri di ping pong da click-tt.ch con analisi avanzate, confronti diretti e monitoraggio delle prestazioni.',
      keywords: 'ping pong svizzera, svizzera ping pong, classifica ping pong svizzera, giocatori ping pong svizzera, classifica elo ping pong, elo ping pong svizzera, click-tt svizzera, stt ping pong, statistiche ping pong svizzera, database ping pong svizzera, ricerca giocatore ping pong svizzera, tennistavolo svizzera, federazione svizzera ping pong',
      ogTitle: 'TTStats - Ping Pong Svizzera Classifiche & Statistiche ELO',
      ogDescription: 'Database completo dei giocatori svizzeri di ping pong con classifiche ELO in diretta, ranghi, storico partite e statistiche avanzate. Segui ogni giocatore in Svizzera.'
    }
  };

const SEOHead: React.FC<SEOHeadProps> = ({ lang }) => {
  const location = useLocation();

  useEffect(() => {
    const data = seoData[lang];

    // Update title
    document.title = data.title;

    // Update meta description
    const descriptionMeta = document.querySelector('meta[name="description"]');
    if (descriptionMeta) {
      descriptionMeta.setAttribute('content', data.description);
    }

    // Update keywords
    const keywordsMeta = document.querySelector('meta[name="keywords"]');
    if (keywordsMeta) {
      keywordsMeta.setAttribute('content', data.keywords);
    }

    // Update Open Graph tags
    const ogTitleMeta = document.querySelector('meta[property="og:title"]');
    if (ogTitleMeta) {
      ogTitleMeta.setAttribute('content', data.ogTitle);
    }

    const ogDescMeta = document.querySelector('meta[property="og:description"]');
    if (ogDescMeta) {
      ogDescMeta.setAttribute('content', data.ogDescription);
    }

    // Update og:url
    const ogUrlMeta = document.querySelector('meta[property="og:url"]');
    if (ogUrlMeta) {
      ogUrlMeta.setAttribute('content', `https://ttstats.ch${location.pathname}`);
    }

    // Update Twitter card tags
    const twitterTitleMeta = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitleMeta) {
      twitterTitleMeta.setAttribute('content', data.ogTitle);
    }

    const twitterDescMeta = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescMeta) {
      twitterDescMeta.setAttribute('content', data.ogDescription);
    }

    // Update HTML lang attribute
    document.documentElement.lang = lang;

    // Add structured data (JSON-LD) for organization and events
    const existingScript = document.getElementById('structured-data');
    if (existingScript) {
      existingScript.remove();
    }

    const structuredData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': 'https://ttstats.ch/#organization',
          name: 'TTStats',
          url: 'https://ttstats.ch',
          logo: 'https://ttstats.ch/logo512.png',
          description: data.description,
          sameAs: [
            'https://apps.apple.com/ch/app/ttstats-swiss-table-tennis/id6744279609',
            'https://play.google.com/store/apps/details?id=com.peaktwilight.ttstats'
          ]
        },
        {
          '@type': 'WebSite',
          '@id': 'https://ttstats.ch/#website',
          url: 'https://ttstats.ch',
          name: 'TTStats',
          description: data.description,
          publisher: {
            '@id': 'https://ttstats.ch/#organization'
          },
          inLanguage: lang
        },
        {
          '@type': 'Event',
          '@id': 'https://ttstats.ch/#kantonalmeisterschaft-2025',
          name: lang === 'de' ? 'Zürcher Kantonalmeisterschaft 2025 (TTVKZ)' :
                lang === 'fr' ? 'Championnat Cantonal de Zurich 2025 (TTVKZ)' :
                lang === 'it' ? 'Campionato Cantonale di Zurigo 2025 (TTVKZ)' :
                'Zurich Cantonal Championship 2025 (TTVKZ)',
          description: lang === 'de' ? 'Offizielle Zürcher Kantonalmeisterschaft 2025 vom Tischtennisverband des Kantons Zürich (TTVKZ). TTStats ist offizieller Partner mit Pro Code Preisen für alle Gewinner.' :
                       lang === 'fr' ? 'Championnat Cantonal de Zurich 2025 organisé par TTVKZ. TTStats est partenaire officiel avec codes Pro pour tous les gagnants.' :
                       lang === 'it' ? 'Campionato Cantonale di Zurigo 2025 organizzato da TTVKZ. TTStats partner ufficiale con codici Pro per tutti i vincitori.' :
                       'Zurich Cantonal Championship 2025 organized by TTVKZ. TTStats is the official partner with Pro codes for all winners.',
          startDate: '2025-10-18T09:00:00+02:00',
          endDate: '2025-10-18T20:00:00+02:00',
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          location: {
            '@type': 'Place',
            name: lang === 'de' ? 'Zürich' : lang === 'fr' ? 'Zurich' : lang === 'it' ? 'Zurigo' : 'Zurich',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Zurich',
              addressRegion: 'ZH',
              addressCountry: 'CH'
            }
          },
          organizer: {
            '@type': 'Organization',
            name: 'TTVKZ - Tischtennisverband des Kantons Zürich',
            url: 'https://ttvkz.ch'
          },
          sponsor: {
            '@id': 'https://ttstats.ch/#organization'
          }
        },
        {
          '@type': 'Event',
          '@id': 'https://ttstats.ch/#anjtt-2025',
          name: lang === 'de' ? 'ANJTT Einzelmeisterschaften 2025' :
                lang === 'fr' ? 'Championnats Individuels ANJTT 2025' :
                lang === 'it' ? 'Campionati Individuali ANJTT 2025' :
                'ANJTT Individual Championships 2025',
          description: lang === 'de' ? 'ANJTT Einzelmeisterschaften 2025 organisiert von CTT Omega Bienne-Péry. TTStats offizieller Partner mit Pro Codes für alle Kategorien und Altersgruppen.' :
                       lang === 'fr' ? 'Championnats Individuels ANJTT 2025 organisés par CTT Omega Bienne-Péry. TTStats partenaire officiel avec codes Pro pour toutes catégories.' :
                       lang === 'it' ? 'Campionati Individuali ANJTT 2025 organizzati da CTT Omega Bienne-Péry. TTStats partner con codici Pro per tutte le categorie.' :
                       'ANJTT Individual Championships 2025 organized by CTT Omega Bienne-Péry. TTStats official partner with Pro codes for all categories.',
          startDate: '2025-11-29T08:00:00+01:00',
          endDate: '2025-11-29T18:00:00+01:00',
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          location: {
            '@type': 'Place',
            name: lang === 'de' ? 'Biel' : 'Bienne',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Bienne',
              addressRegion: 'BE',
              addressCountry: 'CH'
            }
          },
          organizer: {
            '@type': 'Organization',
            name: 'CTT Omega Bienne-Péry',
            url: 'https://cttomega.ch'
          },
          url: 'https://anjtt.ch',
          sponsor: {
            '@id': 'https://ttstats.ch/#organization'
          }
        },
        {
          '@type': 'Event',
          '@id': 'https://ttstats.ch/#schmaetterball-cup-2025',
          name: lang === 'de' ? '54. Schmätterball-Cup 2025' :
                lang === 'fr' ? '54ème Schmätterball-Cup 2025' :
                lang === 'it' ? '54° Schmätterball-Cup 2025' :
                '54th Schmätterball-Cup 2025',
          description: lang === 'de' ? '54. Schmätterball-Cup 2025 vom TTC Ettenhausen. TTStats offizieller Partner mit Pro Abos für Einzel- und Doppel-Gewinner in allen Kategorien A-D.' :
                       lang === 'fr' ? '54ème Schmätterball-Cup 2025 organisé par TTC Ettenhausen. TTStats partenaire avec abos Pro pour simple et double, catégories A-D.' :
                       lang === 'it' ? '54° Schmätterball-Cup 2025 organizzato da TTC Ettenhausen. TTStats partner con abbonamenti Pro per singolo e doppio, categorie A-D.' :
                       '54th Schmätterball-Cup 2025 organized by TTC Ettenhausen. TTStats official partner with Pro subscriptions for singles and doubles winners, categories A-D.',
          startDate: '2025-11-09T07:30:00+01:00',
          endDate: '2025-11-09T18:00:00+01:00',
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          location: {
            '@type': 'Place',
            name: 'Ettenhausen',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Ettenhausen',
              addressRegion: 'TG',
              addressCountry: 'CH'
            }
          },
          organizer: {
            '@type': 'Organization',
            name: 'TTC Ettenhausen',
            url: 'https://ttc-ettenhausen.ch'
          },
          url: 'https://ttc-ettenhausen.ch/index.php/turnier/',
          sponsor: {
            '@id': 'https://ttstats.ch/#organization'
          }
        }
      ]
    };

    const script = document.createElement('script');
    script.id = 'structured-data';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

  }, [lang, location.pathname]);

  return null; // This component doesn't render anything
};

export default SEOHead;
